// src/components/PowerRangeFilter.js
import React from 'react';

const clamp = (v, min, max) => Math.min(max, Math.max(min, v));

export default function PowerRangeFilter({
  minBound = 0,
  maxBound = 5500,
  step = 1,
  value,
  onChange,
  className = '',
}) {
  const min = clamp(value?.min ?? minBound, minBound, maxBound);
  const max = clamp(value?.max ?? maxBound, minBound, maxBound);

  const pct = v => ((v - minBound) / (maxBound - minBound)) * 100;

  const setMin = (v) => {
    const next = clamp(Number(v || 0), minBound, max);
    onChange?.({ min: next, max });
  };
  const setMax = (v) => {
    const next = clamp(Number(v || 0), min, maxBound);
    onChange?.({ min, max: next });
  };

  return (
    <div className={`w-full dual-range ${className}`}>
      {/* PC 유지 / 모바일만 커지는 핸들·트랙 */}
      <style>{`
        .dual-range input[type="range"] {
          pointer-events: none;
          -webkit-appearance: none;
          background: transparent;
        }
        .dual-range input[type="range"]::-webkit-slider-thumb {
          -webkit-appearance: none;
          pointer-events: all;
          width: 16px;
          height: 16px;
          border-radius: 9999px;
          background: #6366f1;
          border: 2px solid #fff;
          box-shadow: 0 0 0 1px rgba(0,0,0,.08);
          position: relative;
        }
        .dual-range input[type="range"]::-moz-range-thumb {
          pointer-events: all;
          width: 16px;
          height: 16px;
          border-radius: 9999px;
          background: #6366f1;
          border: 2px solid #fff;
          box-shadow: 0 0 0 1px rgba(0,0,0,.08);
          position: relative;
        }
        .dual-range input[data-type="min"]::-webkit-slider-thumb { z-index: 30; }
        .dual-range input[data-type="max"]::-webkit-slider-thumb { z-index: 40; }
        .dual-range input[data-type="min"]::-moz-range-thumb { z-index: 30; }
        .dual-range input[data-type="max"]::-moz-range-thumb { z-index: 40; }

        .dual-range input[type="range"]::-webkit-slider-runnable-track { background: transparent; }
        .dual-range input[type="range"]::-moz-range-track { background: transparent; }

        /* ⬇️ 모바일(<640px)에서만 터치 타겟 확장 */
        @media (max-width: 639px) {
          .dual-range input[type="range"]::-webkit-slider-thumb {
            width: 22px; height: 22px;
          }
          .dual-range input[type="range"]::-moz-range-thumb {
            width: 22px; height: 22px;
          }
        }
      `}</style>

      {/* 헤더 + 입력 : 모바일=세로 / PC=가로 */}
      <div className="mb-2 sm:flex sm:items-end sm:justify-between sm:gap-3">
        <div className="mb-2 sm:mb-0">
          <div className="text-sm font-medium text-zinc-800 dark:text-zinc-100">전투력 범위</div>
          <div className="text-sm text-zinc-500 dark:text-zinc-400">
            {minBound} ~ {maxBound} 사이에서 선택
          </div>
        </div>

        {/* 입력: 모바일=2열 그리드(풀폭), PC=가로 일렬 */}
        <div className="grid grid-cols-2 gap-2 sm:flex sm:items-center sm:gap-2 w-full sm:w-auto">
          <label className="text-sm text-zinc-500 dark:text-zinc-400 self-center">MIN</label>
          <input
            type="number"
            inputMode="numeric"
            min={minBound}
            max={maxBound}
            step={step}
            value={min}
            onChange={e => setMin(e.target.value)}
            className="
              w-full sm:w-24 rounded-md border border-zinc-300 bg-white px-3 py-2 text-base
              sm:px-2 sm:py-1 sm:text-sm
              focus:outline-none focus:ring-2 focus:ring-indigo-500
              dark:bg-zinc-800 dark:text-zinc-100 dark:placeholder:text-zinc-400 dark:border-white/10 dark:focus:ring-indigo-400
            "
          />

          <label className="text-sm text-zinc-500 dark:text-zinc-400 self-center">MAX</label>
          <input
            type="number"
            inputMode="numeric"
            min={minBound}
            max={maxBound}
            step={step}
            value={max}
            onChange={e => setMax(e.target.value)}
            className="
              w-full sm:w-24 rounded-md border border-zinc-300 bg-white px-3 py-2 text-base
              sm:px-2 sm:py-1 sm:text-sm
              focus:outline-none focus:ring-2 focus:ring-indigo-500
              dark:bg-zinc-800 dark:text-zinc-100 dark:placeholder:text-zinc-400 dark:border-white/10 dark:focus:ring-indigo-400
            "
          />
        </div>
      </div>

      {/* 슬라이더: 모바일 트랙 높이↑ */}
      <div className="relative h-10 sm:h-8 select-none">
        <div className="absolute left-0 right-0 top-1/2 h-3 sm:h-2 -translate-y-1/2 rounded-full bg-zinc-200 dark:bg-zinc-700" />
        <div
          className="absolute top-1/2 h-3 sm:h-2 -translate-y-1/2 rounded-full bg-indigo-400 dark:bg-indigo-500"
          style={{ left: `${pct(min)}%`, right: `${100 - pct(max)}%` }}
        />
        {/* 좌측 핸들 */}
        <input
          type="range"
          aria-label="최소 전투력"
          data-type="min"
          min={minBound}
          max={maxBound}
          step={step}
          value={min}
          onChange={e => setMin(e.target.value)}
          className="absolute left-0 top-0 h-10 sm:h-8 w-full appearance-none bg-transparent"
        />
        {/* 우측 핸들 */}
        <input
          type="range"
          aria-label="최대 전투력"
          data-type="max"
          min={minBound}
          max={maxBound}
          step={step}
          value={max}
          onChange={e => setMax(e.target.value)}
          className="absolute left-0 top-0 h-10 sm:h-8 w-full appearance-none bg-transparent"
        />
      </div>

      {/* 범위 표시: 모바일 가운데, PC 우측 */}
      <div className="mt-2 text-sm text-zinc-500 dark:text-zinc-400 text-center sm:text-right">
        {String(min)} ~ {String(max)}
      </div>
    </div>
  );
}
